/**
 * Marketer View shell (SYR-68).
 *
 * Isolated dashboard shell mounted at `/marketer-view/:brandHash`. Provides the
 * global data-scope + deep-link-return contexts, the 6-item sidebar, the top
 * bar (brand chip + scope pill), and the routed content Outlet. Existing
 * `/dashboard/*` is untouched — this is reachable by direct URL only for now.
 *
 * Typography: the app's default font (Plus Jakarta Sans) throughout, matching
 * the menu and the existing dashboard pages the destinations reuse. The orbital
 * V3 canvas draws its own font and is unaffected.
 */

import React, { useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { Alert, Drawer, Grid, Spin } from 'antd';
import { MobileOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { Utils } from '../../../utils/utils';
import {
  MarketerScopeProvider,
  useMarketerScope,
} from '../scope/MarketerScopeContext';
import { NavReturnProvider } from '../nav/NavReturnContext';
import { BackBar } from '../nav/BackBar';
import { MarketerSidebar } from './MarketerSidebar';
import { MarketerTopBar } from './MarketerTopBar';
import { ScopeDrawer } from './ScopeDrawer';
import { MarketerOnboardingBanner } from './MarketerOnboardingBanner';
import { MarketerFooter } from './MarketerFooter';
import { THEME } from '../config/theme';

const SIDEBAR_WIDTH = 248;
const SIDEBAR_COLLAPSED = 76;

const MarketerShellInner: React.FunctionComponent = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [mobileBannerDismissed, setMobileBannerDismissed] = useState(
    () => localStorage.getItem('mobileBannerDismissed') === 'true',
  );
  const { activeBrand, brandsLoading } = useMarketerScope();

  const brandHash = activeBrand?.hash ?? '';
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  const content =
    !activeBrand && !brandsLoading ? (
      <div style={{ padding: 48, textAlign: 'center', color: THEME.textSecondary }}>
        <FormattedMessage
          id="marketerView.error.noBrand"
          defaultMessage="No brands available. Add a brand to your organisation first."
        />
      </div>
    ) : brandsLoading && !activeBrand ? (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 96 }}>
        <Spin size="large" />
      </div>
    ) : (
      <Outlet />
    );

  return (
    <div
      className="marketer-view-root"
      style={{
        minHeight: '100vh',
        background: THEME.bgLayout,
        color: THEME.textBase,
        display: 'flex',
      }}
    >
      {/*
        Typography: the whole marketer view uses the app's default font
        (Plus Jakarta Sans), same as the menu and the existing dashboard pages
        the destinations reuse (statements/visualization/etc.). The orbital V3
        is a <canvas> that draws its own font, so it's unaffected. The
        button/input rule just unbreaks the UA reset (controls default to Arial)
        so every control inherits the app font.
      */}
      <style>{`
        .marketer-view-root button,
        .marketer-view-root input,
        .marketer-view-root select,
        .marketer-view-root textarea { font-family: inherit; }

        /* Mobile: the destination components lay out with inline
           grid-template-columns (no breakpoint of their own). Below md, collapse
           every multi-column grid to a single column so nothing overflows or
           crushes on a phone, and tame the largest fixed paddings. */
        @media (max-width: 767px) {
          .marketer-view-root [style*="grid-template-columns"] {
            grid-template-columns: minmax(0, 1fr) !important;
          }
          .marketer-view-content {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }
      `}</style>

      {/* Desktop sidebar */}
      {!isMobile && (
        <div
          style={{
            width: sidebarWidth,
            flexShrink: 0,
            borderRight: `1px solid ${THEME.border}`,
            position: 'sticky',
            top: 0,
            height: '100vh',
            transition: 'width 0.2s cubic-bezier(.4,0,.2,1)',
          }}
        >
          <MarketerSidebar brandHash={brandHash} collapsed={collapsed} />
        </div>
      )}

      {/* Mobile sidebar drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          closable={false}
          width={SIDEBAR_WIDTH}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          styles={{ body: { padding: 0 }, header: { display: 'none' } }}
        >
          <MarketerSidebar
            brandHash={brandHash}
            collapsed={false}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </Drawer>
      )}

      {/* Main column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <MarketerOnboardingBanner />
        <MarketerTopBar
          collapsed={collapsed}
          isMobile={isMobile}
          onToggle={() =>
            isMobile ? setMobileNavOpen(true) : setCollapsed((c) => !c)
          }
          onOpenScope={() => setScopeOpen(true)}
        />
        <main
          className="marketer-view-content"
          style={{
            flex: 1,
            width: '100%',
            maxWidth: 1760,
            margin: '0 auto',
            padding: isMobile ? '20px 16px 64px' : '28px 32px 64px',
            boxSizing: 'border-box',
          }}
        >
          {isMobile && !mobileBannerDismissed && (
            <Alert
              type="info"
              showIcon
              icon={<MobileOutlined />}
              message={
                <FormattedMessage
                  id="onboarding.mobileBanner.title"
                  defaultMessage="Best viewed on desktop"
                />
              }
              description={
                <FormattedMessage
                  id="onboarding.mobileBanner.description"
                  defaultMessage="BrandScience.ai is optimised for larger screens. For the best experience, view this on a desktop or tablet."
                />
              }
              style={{ marginBottom: 16, borderRadius: 12 }}
              closable
              onClose={() => {
                setMobileBannerDismissed(true);
                localStorage.setItem('mobileBannerDismissed', 'true');
              }}
            />
          )}
          <BackBar />
          {content}
        </main>
        <MarketerFooter />
      </div>

      <ScopeDrawer open={scopeOpen} onClose={() => setScopeOpen(false)} />
    </div>
  );
};

export const MarketerShell: React.FunctionComponent = () => {
  const { brandHash } = useParams<{ brandHash: string }>();
  const { user } = useAuth();

  // Auth guards mirror ModernDashboardLayout.
  if (!user) {
    const redirectUrl = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    return <Navigate to={`/sign-in?redirect=${redirectUrl}`} replace />;
  }
  if (Utils.userIsPlatformAdmin(user)) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (Utils.userIsAgencyUser(user) && !user.agencyContext) {
    return <Navigate to="/agency" replace />;
  }

  return (
    <MarketerScopeProvider brandHash={brandHash}>
      <NavReturnProvider>
        <MarketerShellInner />
      </NavReturnProvider>
    </MarketerScopeProvider>
  );
};
